import { useState } from 'react';
import { Check, ChevronsUpDown, PenLine, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { cn } from '../../lib/utils';

export const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
const item = 'text-neutral-200 data-[selected=true]:bg-amber-500/15 data-[selected=true]:text-amber-50';

/** Searchable, grouped dropdown used for every cinematography / catalog choice. `allowCustom` lets the director type their own. */
export function CineCombo({ label, value, options, groups, onChange, placeholder = 'Auto (AI decides)', testId, className, allowCustom, clearLabel = 'Clear — let the AI decide' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const sets = groups || [{ heading: '', items: options || [] }];
  const typed = search.trim();
  const exact = typed && sets.some((g) => g.items.some((o) => o.toLowerCase() === typed.toLowerCase()));
  const pick = (v) => { onChange(v); setOpen(false); setSearch(''); };
  return (
    <div className={cn('min-w-0 space-y-1', className)}>
      {label && <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400">{label}</div>}
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }} modal>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-md border bg-neutral-950 px-2.5 py-2 text-left text-xs transition-colors',
              value ? 'border-amber-400/40 text-amber-100' : 'border-neutral-700 text-neutral-300 hover:border-neutral-500',
            )}
          >
            <span className="truncate">{value || placeholder}</span>
            <ChevronsUpDown size={12} className="shrink-0 opacity-70" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="studio-dark w-[320px] border-neutral-700 bg-neutral-950 p-0 text-neutral-100">
          <Command className="bg-neutral-950 text-neutral-100">
            <CommandInput value={search} onValueChange={setSearch} className="text-neutral-100 placeholder:text-neutral-500"
              placeholder={allowCustom ? `Search or type your own ${(label || 'value').toLowerCase()}…` : `Search ${(label || 'options').toLowerCase()}…`}
              data-testid={testId ? `${testId}-search` : undefined} />
            <CommandList className="max-h-72">
              <CommandEmpty className="py-4 text-center text-xs text-neutral-400">No match.</CommandEmpty>
              {allowCustom && typed && !exact && (
                <CommandItem value={`__custom__ ${typed}`} onSelect={() => pick(typed)} className={cn(item, 'text-amber-200')} data-testid={testId ? `${testId}-custom` : undefined}>
                  <PenLine size={12} className="mr-2" /> Use “{typed}”
                </CommandItem>
              )}
              {value && (
                <CommandItem value="__clear__" onSelect={() => pick('')} className={cn(item, 'text-neutral-400')}>
                  <X size={12} className="mr-2" /> {clearLabel}
                </CommandItem>
              )}
              {sets.map((g) => (
                <CommandGroup key={g.heading || 'all'} heading={g.heading || undefined} className="[&_[cmdk-group-heading]]:text-amber-300/80">
                  {g.items.map((o) => (
                    <CommandItem
                      key={`${g.heading}-${o}`}
                      value={`${g.heading} ${o}`}
                      onSelect={() => pick(o)}
                      className={item}
                      data-testid={testId ? `${testId}-opt-${slug(o)}` : undefined}
                    >
                      <Check size={12} className={cn('mr-2 text-amber-300', value === o ? 'opacity-100' : 'opacity-0')} />
                      {o}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
